import React from 'react';
import { Box, Container, Grid, IconButton, Link, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import InstagramIcon from '@mui/icons-material/Instagram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { business, colors } from '../theme/tokens';
import Logo from './ui/Logo';

const serviceLinks = [
  { label: 'Plumber in Kingsbury', href: '/plumbing' },
  { label: 'Kitchen renovations', href: '/renovations#kitchen' },
  { label: 'Bathroom renovations', href: '/renovations#bathroom' },
  { label: 'House renovations', href: '/renovations#house-renovation' },
  { label: 'Property maintenance NW9', href: '/maintenance' },
];

const companyLinks = [
  { label: 'About Behson', href: '/about' },
  { label: 'Service areas', href: '/locations' },
  { label: 'Portfolio', href: '/portfolio' },
  { label: 'Gallery', href: '/gallery' },
  { label: 'Reviews', href: '/testimonials' },
  { label: 'Contact & free quote', href: '/contact' },
];

const FooterLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => (
  <Link
    component={RouterLink}
    to={to}
    underline="none"
    sx={{
      display: 'inline-block',
      color: colors.textMuted,
      fontSize: '0.95rem',
      py: 0.5,
      transition: 'color .2s ease, transform .2s ease',
      '&:hover': { color: colors.white, transform: 'translateX(4px)' },
    }}
  >
    {children}
  </Link>
);

const ColumnTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography component="h2" variant="overline" sx={{ color: colors.textSubtle, display: 'block', mb: 1.5 }}>
    {children}
  </Typography>
);

const Footer = () => (
  <Box component="footer" sx={{ backgroundColor: '#0d0d0d', borderTop: `1px solid ${colors.border}`, pt: { xs: 8, md: 10 }, pb: 4 }}>
    <Container maxWidth="lg">
      <Grid container spacing={{ xs: 5, md: 6 }}>
        <Grid item xs={12} md={4}>
          <Logo height={48} />
          <Typography sx={{ color: colors.textMuted, mt: 3, maxWidth: 320 }}>
            Plumbing, renovation and property maintenance for homes across Kingsbury, NW9 and North West London. Licensed, insured and local.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <IconButton
              component="a"
              href={business.instagramHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Follow us on Instagram @behsonuk"
              sx={{ border: `1px solid ${colors.border}`, '&:hover': { borderColor: colors.white } }}
            >
              <InstagramIcon />
            </IconButton>
            <IconButton
              component="a"
              href={business.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contact us on WhatsApp"
              sx={{ border: `1px solid ${colors.border}`, '&:hover': { borderColor: colors.white } }}
            >
              <WhatsAppIcon />
            </IconButton>
          </Box>
        </Grid>

        <Grid item xs={6} sm={4} md={2.5}>
          <ColumnTitle>Services</ColumnTitle>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid' }}>
            {serviceLinks.map((l) => (
              <li key={l.href}>
                <FooterLink to={l.href}>{l.label}</FooterLink>
              </li>
            ))}
          </Box>
        </Grid>

        <Grid item xs={6} sm={4} md={2.5}>
          <ColumnTitle>Company</ColumnTitle>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid' }}>
            {companyLinks.map((l) => (
              <li key={l.href}>
                <FooterLink to={l.href}>{l.label}</FooterLink>
              </li>
            ))}
          </Box>
        </Grid>

        <Grid item xs={12} sm={4} md={3}>
          <ColumnTitle>Contact</ColumnTitle>
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Link href={business.phoneHref} underline="none" sx={{ display: 'flex', gap: 1.5, alignItems: 'center', color: colors.text, fontWeight: 600 }}>
              <PhoneIcon sx={{ fontSize: 20, color: colors.textMuted }} /> {business.phoneDisplay}
            </Link>
            <Link href={business.emailHref} underline="none" sx={{ display: 'flex', gap: 1.5, alignItems: 'center', color: colors.text, fontWeight: 600, wordBreak: 'break-all' }}>
              <EmailOutlinedIcon sx={{ fontSize: 20, color: colors.textMuted }} /> {business.email}
            </Link>
            <Typography sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', color: colors.textMuted }}>
              <PlaceOutlinedIcon sx={{ fontSize: 20, mt: 0.3 }} /> {business.areaShort}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      <Box
        sx={{
          mt: { xs: 6, md: 8 },
          pt: 3,
          borderTop: `1px solid ${colors.border}`,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" sx={{ color: colors.textSubtle }}>
          © {new Date().getFullYear()} {business.name}. Licensed & insured. Serving Kingsbury, Colindale, Edgware, Queensbury and North West London.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography
            component={RouterLink}
            to="/privacy"
            variant="body2"
            sx={{ color: colors.textSubtle, textDecoration: 'none', '&:hover': { color: colors.white } }}
          >
            Privacy policy
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSubtle }}>
            Website by Arman Sorour
          </Typography>
        </Box>
      </Box>
    </Container>
  </Box>
);

export default Footer;
